interface WizardStepperProps {
  steps: Array<{ id: string; label: string }>;
  currentStep: number;
  completedSteps: number[];
  onStepClick?: (index: number) => void;
}

export default function WizardStepper({ steps, currentStep, completedSteps, onStepClick }: WizardStepperProps) {
  return (
    <>
      {/* Desktop: full horizontal stepper */}
      <div
        className="hidden sm:flex"
        style={{ alignItems: 'flex-start', gap: 0, marginBottom: 32 }}
        role="list"
        aria-label="Wizard steps"
      >
        {steps.map((step, index) => {
          const isCompleted = completedSteps.includes(index);
          const isActive    = index === currentStep;
          const isLast      = index === steps.length - 1;
          const isClickable = isCompleted && !isActive && !!onStepClick;

          const circleStyle: React.CSSProperties = isCompleted
            ? { background: 'var(--primary)', color: '#fff', border: '2px solid var(--primary)' }
            : isActive
            ? { background: '#fff', color: 'var(--primary)', border: '2px solid var(--primary)' }
            : { background: 'var(--surface-alt)', color: 'var(--text-secondary)', border: '2px solid var(--border)' };

          const labelStyle: React.CSSProperties = {
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            fontWeight: isActive ? 700 : 500,
            color: isCompleted || isActive ? 'var(--primary)' : 'var(--text-secondary)',
            marginTop: 6,
            textAlign: 'center',
            maxWidth: 80,
          };

          const lineColor = isCompleted ? 'var(--primary)' : 'var(--border)';

          return (
            <div
              key={step.id}
              role="listitem"
              aria-current={isActive ? 'step' : undefined}
              onClick={isClickable ? () => onStepClick(index) : undefined}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative',
                cursor: isClickable ? 'pointer' : 'default',
              }}
            >
              {/* Connector line left */}
              {index > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 14,
                    left: 0,
                    width: '50%',
                    height: 2,
                    background: lineColor,
                    transition: 'background 200ms',
                  }}
                />
              )}
              {/* Connector line right */}
              {!isLast && (
                <div
                  style={{
                    position: 'absolute',
                    top: 14,
                    right: 0,
                    width: '50%',
                    height: 2,
                    background: completedSteps.includes(index) ? 'var(--primary)' : 'var(--border)',
                    transition: 'background 200ms',
                  }}
                />
              )}

              {/* Circle */}
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--font-display)',
                  fontSize: 13,
                  fontWeight: 700,
                  zIndex: 1,
                  transition: 'background 200ms, border-color 200ms, transform 150ms',
                  ...(isClickable ? { transform: 'scale(1)' } : {}),
                  ...circleStyle,
                }}
                onMouseEnter={isClickable ? (e) => { e.currentTarget.style.transform = 'scale(1.12)'; } : undefined}
                onMouseLeave={isClickable ? (e) => { e.currentTarget.style.transform = 'scale(1)'; } : undefined}
              >
                {isCompleted ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2 7l3.5 3.5L12 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  index + 1
                )}
              </div>

              {/* Label */}
              <p style={labelStyle}>{step.label}</p>
            </div>
          );
        })}
      </div>

      {/* Mobile: compact current step indicator */}
      <div
        className="flex sm:hidden"
        style={{
          alignItems: 'center',
          gap: 8,
          marginBottom: 24,
          padding: '10px 14px',
          background: 'var(--surface-alt)',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'var(--primary)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {currentStep + 1}
        </div>
        <div>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
            {steps[currentStep]?.label}
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>
      </div>
    </>
  );
}
